import React, { useCallback, type FC } from 'react'
import { styled } from '@linaria/react'
import { Input } from '../ui/Input'
import { Tag } from '../ui/Tag'
import { Typography } from '../ui/Typography'
import { colors } from '../styles/colors'
import { fromNano } from '@ton/core'

export const AmountInputWithPresets: FC<{
  maxValue: string
  minValue: string
  value: string
  onChange?: (value: string) => void
  placeholder?: string
  presets?: number[]
  className?: string
  'data-testid'?: string
}> = ({
  value,
  onChange,
  placeholder = '1',
  presets = [1, 5, 10, 25, 50],
  className,
  maxValue,
  minValue,
  'data-testid': dataTestId,
}) => {
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value

      if (newValue === '') {
        onChange?.(newValue)
        return
      }

      // Regex to match numbers with at most 1 decimal place
      const decimalRegex = /^\d+(\.\d{0,1})?$/

      if (decimalRegex.test(newValue)) {
        onChange?.(newValue)
      }
    },
    [onChange],
  )

  const handlePresetClick = useCallback(
    (presetValue: string) => {
      onChange?.(presetValue)
    },
    [onChange],
  )

  const filteredPresets = maxValue
    ? presets.filter(
        preset =>
          preset <= Number(fromNano(maxValue)) &&
          preset >= Number(fromNano(minValue)),
      )
    : presets

  return (
    <Container className={className}>
      <InputContainer>
        <StyledInput
          data-testid={dataTestId}
          max={fromNano(maxValue)}
          min={fromNano(minValue)}
          onChange={handleInputChange}
          placeholder={placeholder}
          step="0.1"
          type="number"
          value={value}
        />
        <TonLabel>
          <Typography
            fontSize={16}
            fontWeight={400}
            color={colors.textSecondary}
          >
            TON
          </Typography>
        </TonLabel>
      </InputContainer>

      <PresetsContainer>
        {filteredPresets.map(preset => (
          <StyledTag
            key={preset}
            label={preset.toString()}
            isActive={Number(value) === preset}
            onClick={() => handlePresetClick(String(preset))}
          />
        ))}
      </PresetsContainer>
    </Container>
  )
}

const Container = styled.div`
  display: grid;
  gap: 16px;
`

const InputContainer = styled.div`
  position: relative;
  width: 100%;
`

const StyledInput = styled(Input)`
  padding-right: 60px;
  text-align: left;
  border: 1px solid ${colors.grey300};

  /* Hide number input arrows */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
  }
`

const TonLabel = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`

const PresetsContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 8px;
  justify-content: start;
  overflow: scroll;
`

const StyledTag = styled(Tag)`
  padding: 0 16px;
  width: 60px;
  height: 25px;
`
